import React, { ReactNode, useEffect, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { A11y, Navigation, Pagination, Thumbs, Zoom } from 'swiper/modules'
import { MenuItemType } from '@utils/types'
import { Modal, WImage } from '@components/atoms'
import { GenericSlider } from '@components/organisms'
import styles from './GalleryModal.module.sass'

interface Props {
  id: string
  items?: MenuItemType[]
  initialSlide?: number
  zoom?: boolean
  paginationStyle?: 'thumbs' | 'default'
  open?: boolean
  onClose: () => void
  children?: ReactNode
  className?: string
  mainSwiperClassName?: string
}

const GalleryModal = (props: Props) => {
  const {
    id = undefined,
    items = [],
    initialSlide = 0,
    zoom = false,
    paginationStyle = 'default',
    open = false,
    onClose = undefined,
    children = undefined,
    className = '',
    mainSwiperClassName = undefined,
  } = props

  const [thumbsSwiper, setThumbsSwiper] = useState(null)
  const [currentSlide, setCurrentSlide] = useState(0)

  // Ogni volta che si chiude la modale, va resettato thumbsSwiper altrimenti crasha
  useEffect(() => {
    if (!open) setThumbsSwiper(null)
  }, [open])

  const modules = [Thumbs, Navigation, A11y]
    .concat(paginationStyle !== 'thumbs' ? [Pagination] : [])
    .concat(zoom ? [Zoom] : [])

  return (
    <Modal
      id={id}
      open={open}
      onClose={onClose}
      closeButton
      closeClassName={styles.close}
      className={`${styles.root} ${styles[`root--pagination-${paginationStyle}`]} ${className}`}
    >
      {open && items?.length && (
        <div className={`${styles.body} swiperScope--dark`}>
          <div className={styles.main}>
            <GenericSlider
              id={`${id}_main`}
              conf={{
                modules: modules,
                className: styles.swiper,
                slidesPerView: 1,
                initialSlide: initialSlide,
                freeMode: true,
                zoom: zoom || undefined,
                watchSlidesProgress: true,
                onSlideChange: (s) => setCurrentSlide(s.activeIndex),
                thumbs: { swiper: thumbsSwiper },
                navigation: true,
                pagination:
                  paginationStyle !== 'thumbs'
                    ? {
                        clickable: true,
                        el: `#${id}_pagination`,
                      }
                    : undefined,
              }}
              theme="dark"
              className={`${styles.mainSwiper} ${mainSwiperClassName}`}
              navClassName={styles.mainSwiperNav}
            >
              {children}
            </GenericSlider>
          </div>
          {paginationStyle !== 'thumbs' && (
            <div id={`${id}_pagination`} className={`${styles.pagination} swiper-pagination`}></div>
          )}
          <Swiper
            className={styles.thumbs}
            slidesPerView={'auto'}
            spaceBetween={5}
            freeMode={true}
            modules={[A11y]}
            watchSlidesProgress={true}
            onSwiper={setThumbsSwiper}
            centeredSlidesBounds={true}
            centerInsufficientSlides={true}
          >
            {items.map((x, index) => (
              <SwiperSlide
                key={x.pk}
                className={`${styles.thumbsSlide} ${
                  currentSlide === index ? styles['thumbsSlide--active'] : ''
                }`}
              >
                <WImage
                  src={x.image}
                  alt={x?.imageAlt}
                  maxWidth={98}
                  maxHeight={98}
                  objectFit="cover"
                  className={styles.thumbsImage}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </Modal>
  )
}

export default GalleryModal
